import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/auth";
import { sincronizarSenhaComMemberkit } from "../lib/memberkit";
import { criarSenhaLiberada } from "../lib/senhaLiberada";
import { LogoCF } from "../componentes/ui";
import { FALE_CONOSCO } from "../componentes/Rodape";

// A senha criada sem e-mail, depois de o suporte liberar (000383).
//
// É o caminho de quem não recebe o e-mail do Auth (primeiro acesso, convite ou
// redefinição). O suporte libera no dossiê, e por 1 hora, uma vez só, o PRÓPRIO
// aluno cria a senha aqui. O atendente só diz o endereço desta página.
//
// Página pública, como a de entrada: quem chega aqui não tem sessão. Quem já está
// logado vai para a inicial, porque a senha dele se troca pelo perfil.
export default function SenhaLiberada() {
  const { sessao } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [confirma, setConfirma] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // `ocupado` segura o desvio enquanto a senha vai para o Memberkit: a entrada
  // logo abaixo cria a sessão antes de a sincronização terminar.
  if (sessao && !ocupado) return <Navigate to="/" replace />;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setAviso(null);
    if (senha !== confirma) {
      setErro("As duas senhas não conferem.");
      return;
    }
    setOcupado(true);
    const emailLimpo = email.trim().toLowerCase();
    const r = await criarSenhaLiberada(emailLimpo, senha);

    if (r.resultado !== "ok") {
      setOcupado(false);
      if (r.resultado === "sem_liberacao") {
        // A mesma frase para todo "não" (e-mail errado, liberação vencida, usada ou
        // cancelada): distinguir diria a quem testa e-mails quem é aluno.
        setErro(
          "Não há liberação valendo para este e-mail. Confira o endereço: tem de ser o e-mail da sua compra. "
            + "A liberação vale por 1 hora e uma vez só; se ela passou, peça ao suporte para liberar de novo.",
        );
      } else if (r.resultado === "senha_fraca") {
        setErro("Essa senha foi recusada por ser fácil de adivinhar. Escolha outra; a liberação continua valendo.");
      } else if (r.resultado === "pedido_invalido") {
        setErro(r.detalhe.charAt(0).toUpperCase() + r.detalhe.slice(1) + ".");
      } else {
        setErro("Não foi possível criar a senha agora. A liberação continua valendo: tente de novo em instantes.");
      }
      return;
    }

    // A senha vale. Entrar com ela já aqui poupa o aluno de digitar tudo de novo.
    const { error } = await supabase.auth.signInWithPassword({ email: emailLimpo, password: senha });
    if (error) {
      setOcupado(false);
      setSenha("");
      setConfirma("");
      setAviso("Senha criada. Entre com ela na tela de entrada.");
      return;
    }
    // Com await, como na redefinição: a pessoa vai usar a senha nas aulas em
    // seguida. Se o Memberkit falhar, o carimbo fica nulo e o próximo login reempurra.
    await sincronizarSenhaComMemberkit(senha, "redefinicao");
    navigate("/", { replace: true });
  }

  const campo =
    "h-12 w-full rounded-xl border-[1.5px] border-borda bg-superficie px-4 text-tinta transition focus:border-laranja focus:ring-3 focus:ring-laranja/20 focus:outline-none";

  return (
    <div className="painel-navy flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <LogoCF className="mx-auto mb-7 h-8" />
        <div className="surface p-7 sm:p-8">
          <h1 className="text-2xl font-bold text-titulo">Criar a senha liberada pelo suporte</h1>
          <p className="mt-1 text-sm text-cinza">
            Use esta página quando o suporte liberar a sua senha. A liberação vale por 1 hora e
            uma vez só.
          </p>

          <form onSubmit={enviar} className="mt-5 flex flex-col gap-4">
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-tinta">E-mail da compra</span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seuemail@exemplo.com.br"
                className={`${campo} placeholder:text-cinza/60`}
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-tinta">Nova senha</span>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                className={campo}
              />
              <span className="mt-1.5 block text-sm text-cinza">Mínimo de 8 caracteres.</span>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-tinta">Repita a senha</span>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={confirma}
                onChange={(e) => setConfirma(e.target.value)}
                className={campo}
              />
            </label>

            {erro && (
              <p role="alert" className="rounded-xl bg-erro-fundo p-3.5 text-sm leading-relaxed text-erro-tinta">
                {erro}
              </p>
            )}
            {aviso && (
              <p role="status" className="rounded-xl bg-aviso-fundo p-3.5 text-sm leading-relaxed text-aviso-tinta">
                {aviso}
              </p>
            )}

            <button type="submit" disabled={ocupado} className="pill-cta w-full py-3.5 text-base">
              {ocupado ? "Aguarde…" : "Criar a senha e entrar"}
            </button>
          </form>

          <Link
            to="/entrar"
            className="mt-5 block w-full text-center text-sm font-semibold text-link underline underline-offset-3"
          >
            Voltar para a entrada
          </Link>
        </div>

        <p className="mt-5 text-center text-sm text-navy-mute">
          Ainda não falou com o suporte?{" "}
          <a
            href={FALE_CONOSCO}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-white underline underline-offset-4 transition hover:text-laranja"
          >
            Fale com o nosso suporte
            <span className="sr-only"> (abre em outra aba)</span>
          </a>
        </p>
      </div>
    </div>
  );
}
